import { useState, useRef, useEffect } from 'react';
import { FORM_CONFIG } from '../config/formConfig';
import { useRecaptcha } from '../hooks/useRecaptcha';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface DemoData {
  name: string;
  email: string;
  phone: string;
  program: string;
  message: string;
}

// Knowledge base based on site content
const knowledgeBase = {
  greetings: [
    "Hello! I'm here to help you learn about our chronic care management services. How can I assist you today?",
    "Hi there! Welcome to Chronicity Health. What would you like to know about our services?",
    "Hello! I can help answer questions about our programs, services, or help you schedule a demo. What can I help with?"
  ],
  programs: {
    ccm: "CCM (Chronic Care Management) provides coordinated care for patients with multiple chronic conditions. Our comprehensive approach combines medical expertise, behavioral health, nutrition, and pharmacy services to deliver measurable outcomes. Would you like to learn more or schedule a demo?",
    rpm: "RPM (Remote Patient Monitoring) uses FDA-approved medical devices to track vital signs and health metrics in real-time. This continuous monitoring allows our care team to detect changes early and intervene before conditions worsen. Common monitored conditions include hypertension, diabetes, COPD, and congestive heart failure. Interested in learning more?",
    bhi: "BHI (Behavioral Health Integration) seamlessly integrates mental health services with primary care. Our licensed therapists and psychiatric specialists address mental health factors that impact chronic disease outcomes. Services include individual therapy, psychiatric consultation, care coordination, and crisis support. Would you like more details?",
    tcm: "TCM (Transitional Care Management) provides comprehensive support during transitions from hospital to home, reducing readmissions and ensuring continuity of care. Our program includes initial contact within 48 hours, medication reconciliation, follow-up visits, and ongoing monitoring. This helps reduce hospital readmissions by 35%. Want to know more?",
    pcm: "PCM (Principal Care Management) offers focused, intensive management for patients with a single high-risk chronic condition requiring substantial care coordination. This program provides specialized attention for patients who need more than routine care but may not have multiple conditions. Interested in scheduling a demo?"
  },
  services: "We offer comprehensive chronic care management services including Internal Medicine, Behavioral Health, Clinical Nutrition, Clinical Pharmacy, and Care Coordination. Our multidisciplinary team works together to address every aspect of chronic disease management. All specialists collaborate in real-time, sharing insights and coordinating interventions for optimal outcomes.",
  technology: "Our platform features automated tracking, smart alerts, EHR integration (works with Epic, athenahealth, eClinicalWorks, Cerner, and more), and telehealth capabilities. Implementation typically takes 3-4 weeks with zero burden on your practice.",
  results: "Our programs show measurable results: 35% reduction in hospital readmissions, 24% fewer emergency room visits, $40+ monthly revenue per patient, and full implementation in 3-4 weeks.",
  contact: "You can reach us at info@setupccm.com or call (844) 422-6633. We're located at 29436 Ford Rd., Garden City, MI 48135. Would you like to schedule a demo?",
  demo: "Great! I can help you schedule a demo. Let me collect some information from you.",
  default: "I can help you with information about our programs (CCM, RPM, BHI, TCM, PCM), services, technology, results, or help you schedule a demo. What would you like to know more about?"
};

const demoKeywords = ['demo', 'schedule', 'request', 'book', 'appointment', 'meeting', 'contact', 'speak', 'talk', 'call', 'interested', 'want to learn'];

function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Hello! I'm your virtual assistant. I can help answer questions about our chronic care management services or help you schedule a demo. How can I assist you?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [demoData, setDemoData] = useState<DemoData>({
    name: '',
    email: '',
    phone: '',
    program: '',
    message: ''
  });
  const [currentDemoField, setCurrentDemoField] = useState<keyof DemoData | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize reCAPTCHA
  const { executeRecaptcha } = useRecaptcha({
    siteKey: FORM_CONFIG.RECAPTCHA_SITE_KEY,
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const detectDemoRequest = (text: string): boolean => {
    const lowerText = text.toLowerCase();
    return demoKeywords.some(keyword => lowerText.includes(keyword));
  };

  const generateResponse = (userMessage: string): string => {
    const lowerMessage = userMessage.toLowerCase();

    // Check for demo requests
    if (detectDemoRequest(lowerMessage)) {
      setDemoMode(true);
      setCurrentDemoField('name');
      return "I'd be happy to help you schedule a demo! Let's get started. What's your full name?";
    }

    // Check for program queries
    if (lowerMessage.includes('ccm') || lowerMessage.includes('chronic care')) {
      return knowledgeBase.programs.ccm;
    }
    if (lowerMessage.includes('rpm') || lowerMessage.includes('remote') || lowerMessage.includes('monitoring')) {
      return knowledgeBase.programs.rpm;
    }
    if (lowerMessage.includes('bhi') || lowerMessage.includes('behavioral') || lowerMessage.includes('mental health')) {
      return knowledgeBase.programs.bhi;
    }
    if (lowerMessage.includes('tcm') || lowerMessage.includes('transitional')) {
      return knowledgeBase.programs.tcm;
    }
    if (lowerMessage.includes('pcm') || lowerMessage.includes('principal')) {
      return knowledgeBase.programs.pcm;
    }

    // Check for other topics
    if (lowerMessage.includes('service') || lowerMessage.includes('what do you offer')) {
      return knowledgeBase.services;
    }
    if (lowerMessage.includes('technology') || lowerMessage.includes('system') || lowerMessage.includes('platform')) {
      return knowledgeBase.technology;
    }
    if (lowerMessage.includes('result') || lowerMessage.includes('outcome') || lowerMessage.includes('improvement')) {
      return knowledgeBase.results;
    }
    if (lowerMessage.includes('contact') || lowerMessage.includes('email') || lowerMessage.includes('phone') || lowerMessage.includes('address')) {
      return knowledgeBase.contact;
    }
    if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey')) {
      return knowledgeBase.greetings[Math.floor(Math.random() * knowledgeBase.greetings.length)];
    }

    return knowledgeBase.default;
  };

  const handleDemoField = (value: string): string => {
    if (!currentDemoField) return "";

    // Update the current field
    const updatedData = { ...demoData, [currentDemoField]: value };
    setDemoData(updatedData);

    const fields: (keyof DemoData)[] = ['name', 'email', 'phone', 'program', 'message'];
    const currentIndex = fields.indexOf(currentDemoField);
    const nextIndex = currentIndex + 1;

    if (nextIndex < fields.length) {
      const nextField = fields[nextIndex];
      setCurrentDemoField(nextField);
      
      const fieldPrompts: Record<keyof DemoData, string> = {
        name: "What's your full name?",
        email: "What's your email address?",
        phone: "What's your phone number?",
        program: "Which program are you interested in? (CCM, RPM, BHI, TCM, PCM, or General Inquiry)",
        message: "Any additional message or questions? (Optional, type 'skip' to skip)"
      };

      return fieldPrompts[nextField];
    } else {
      // All fields collected, submit demo
      setCurrentDemoField(null);
      // Update demoData one more time with the last field value
      setDemoData(updatedData);
      // Trigger submission after state update
      setTimeout(() => submitDemo(), 100);
      return "Thank you! Submitting your request...";
    }
  };

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    return /^[\d\s\-\(\)]+$/.test(phone) && phone.replace(/\D/g, '').length >= 10;
  };

  const submitDemo = async () => {
    setIsTyping(true);
    
    // Use a callback to get the latest state
    setDemoData(currentData => {
      // Validate required fields
      if (!currentData.name || !currentData.email || !currentData.phone) {
        addMessage('bot', "I need your name, email, and phone number to schedule a demo. Let's start over. What's your full name?");
        setCurrentDemoField('name');
        setIsTyping(false);
        return { name: '', email: '', phone: '', program: '', message: '' };
      }

      if (!validateEmail(currentData.email)) {
        addMessage('bot', "That email doesn't look valid. Please provide a valid email address.");
        setCurrentDemoField('email');
        setIsTyping(false);
        return currentData;
      }

      if (!validatePhone(currentData.phone)) {
        addMessage('bot', "That phone number doesn't look valid. Please provide a valid phone number.");
        setCurrentDemoField('phone');
        setIsTyping(false);
        return currentData;
      }

      // Execute reCAPTCHA and submit the form
      const submitWithRecaptcha = async () => {
        let recaptchaToken = null;
        if (FORM_CONFIG.RECAPTCHA_SITE_KEY) {
          recaptchaToken = await executeRecaptcha();
          if (!recaptchaToken) {
            console.warn('reCAPTCHA token not obtained, but continuing with submission');
          }
        }

        const apiUrl = FORM_CONFIG.NODE_FORM_URL;
        
        return fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...currentData,
            source: 'Chatbot',
            recaptchaToken: recaptchaToken
          })
        });
      };

      submitWithRecaptcha()
        .then(async response => {
          const data = await response.json();
          if (response.ok && data.success) {
            addMessage('bot', "Perfect! I've submitted your demo request. Our team will contact you shortly. Thank you for your interest in Chronicity Health! Is there anything else I can help you with?");
            setDemoMode(false);
            setCurrentDemoField(null);
            setIsTyping(false);
            return { name: '', email: '', phone: '', program: '', message: '' };
          } else {
            addMessage('bot', "I encountered an issue submitting your request. Please try contacting us directly at info@setupccm.com or call (844) 422-6633.");
            setIsTyping(false);
            return currentData;
          }
        })
        .catch(error => {
          console.error('Chatbot form submission error:', error);
          addMessage('bot', "I encountered an issue submitting your request. Please try contacting us directly at info@setupccm.com or call (844) 422-6633.");
          setIsTyping(false);
          return currentData;
        });

      return currentData;
    });
  };

  const addMessage = (sender: 'user' | 'bot', text: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      text,
      sender,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMessage = inputValue.trim();
    addMessage('user', userMessage);
    const currentInput = inputValue.trim();
    setInputValue('');

    setIsTyping(true);
    
    // Simulate typing delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    let botResponse: string;

    if (demoMode && currentDemoField) {
      if (currentInput.toLowerCase() === 'skip' && currentDemoField === 'message') {
        botResponse = handleDemoField('');
      } else {
        botResponse = handleDemoField(currentInput);
      }
      
      if (botResponse) {
        addMessage('bot', botResponse);
      }
      setIsTyping(false);
      return;
    } else {
      botResponse = generateResponse(currentInput);
      addMessage('bot', botResponse);
      setIsTyping(false);
    }
  };

  const handleRequestDemo = () => {
    setIsOpen(true);
    setDemoMode(true);
    setCurrentDemoField('name');
    addMessage('bot', "Great! I'd be happy to help you schedule a demo. Let's get started. What's your full name?");
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-14 h-14 sm:w-16 sm:h-16 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 focus:outline-none focus:ring-4 focus:ring-emerald-300"
          aria-label="Open chat"
        >
          <i className="ri-customer-service-2-line text-xl sm:text-2xl"></i>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-full sm:max-w-md h-[calc(100vh-8rem)] sm:h-[600px] sm:max-h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-customer-service-2-line text-xl"></i>
              </div>
              <div>
                <h3 className="font-semibold">Chronicity Health</h3>
                <p className="text-xs text-emerald-100">We're here to help</p>
              </div>
            </div>
            <button
              onClick={() => {
                setIsOpen(false);
                setDemoMode(false);
                setCurrentDemoField(null);
                setDemoData({ name: '', email: '', phone: '', program: '', message: '' });
              }}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              aria-label="Close chat"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    message.sender === 'user'
                      ? 'bg-emerald-700 text-white rounded-br-sm'
                      : 'bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-200'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 rounded-2xl rounded-bl-sm shadow-sm border border-gray-200 px-4 py-2">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-200 p-4 bg-white rounded-b-2xl">
            {messages.length === 1 && (
              <div className="mb-3">
                <button
                  onClick={handleRequestDemo}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-sm flex items-center justify-center gap-2"
                >
                  <i className="ri-calendar-line"></i>
                  Request Demo
                </button>
              </div>
            )}
            <form onSubmit={handleSend} className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={demoMode && currentDemoField ? `Enter your ${currentDemoField}...` : "Type your message..."}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                disabled={isTyping}
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isTyping}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Send message"
              >
                <i className="ri-send-plane-line"></i>
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Chatbot;

